<script lang="ts">
  import Icon from '$shared/atoms/Icon/Icon.svelte';
  import Spinner from '$shared/atoms/Spinner/Spinner.svelte';
  import { SearchStyle } from '$shared/constants/SearchStyle';

  interface Props {
    delay?: number;
    loading?: boolean;
    disabled?: boolean;
    minLength?: number;
    placeholder?: string | null;
    name?: string;
    style?: SearchStyle;
    height?: 'large' | 'normal' | 'small';
    onchange?: (detail: string) => void;
  }

  let {
    delay = 500,
    loading = false,
    disabled = false,
    minLength = 0,
    placeholder = null,
    name = 'name',
    style = SearchStyle.None,
    height = 'normal',
    onchange
  }: Props = $props();

  let value = $state('');
  let allowFocus = $state(false);
  let inputElement: HTMLInputElement;

  export const reset = () => {
    value = '';
  };
  export const setValue = (val: string) => {
    value = val;
  };

  const debounce = (func: (...args: unknown[]) => void, ms: number) => {
    let debounceTimer: ReturnType<typeof setTimeout>;
    return function (this: unknown, ...args: unknown[]) {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => func.apply(this, args), ms);
    };
  };

  const handleSearch = (): void => {
    allowFocus = true;
    if (!value.trim()) {
      onchange?.('');
      return;
    }
    if (value.trim() && value.length >= minLength) {
      onchange?.(value);
    }
  };

  const onInputChange = debounce(handleSearch, delay);
</script>

<div
  class="comp-search {style}"
  class:disabled
  class:height-large={height === 'large'}
  class:height-small={height === 'small'}
>
  <input
    type="text"
    bind:value
    bind:this={inputElement}
    {disabled}
    placeholder={placeholder || 'Search'}
    name="search"
    autocomplete="off"
    oninput={onInputChange}
    data-cy={name}
  />
  <div class="icon" class:height-large={height === 'large'} class:height-small={height === 'small'}>
    {#if loading}
      <Spinner />
    {:else}
      <Icon value="icon-search grey" size="18px" fontSize="18px" />
    {/if}
  </div>
</div>

<style>
  .comp-search {
    width: 100%;
    min-width: 140px;
    background-color: #fff;
    display: flex;
    flex-direction: row-reverse;
    align-items: center;
    border-radius: 5px;
    overflow: hidden;
    position: relative;
    height: 34px;
  }

  .comp-search.height-large {
    height: 44px;
  }

  .comp-search.height-small {
    height: 28px;
  }

  .comp-search.disabled {
    background-color: var(--grey-light) !important;
    cursor: not-allowed;
    color: #fff !important;
  }

  .comp-search.input {
    border: 1px solid rgba(0, 0, 0, 0.15);
    background-color: #fff !important;
    height: 36px;
  }
  .comp-search.input input,
  .comp-search.input .icon {
    background-color: #fff !important;
  }

  .comp-search input:disabled,
  .comp-search.disabled .icon {
    cursor: not-allowed;
    background-color: var(--grey-light) !important;
    color: #fff !important;
  }

  .icon {
    flex-basis: 34px;
    flex-shrink: 0;
    width: 34px;
    height: 34px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--grey-bg);
  }

  .icon.height-large {
    height: 44px;
  }

  .icon.height-small {
    height: 28px;
  }

  .comp-search input {
    width: 100%;
    min-width: 100px;
    height: inherit;
    border: 0;
    outline: 0;
    background-color: var(--grey-bg);
    flex: 1;
    transition: min-width 0.2s linear;
  }

  .comp-search:hover input,
  .comp-search:hover .icon {
    background-color: var(--grey-xlight);
  }

  .comp-search input:focus,
  .comp-search input:focus + .icon {
    background-color: var(--light-blue);
  }

  .comp-search input:focus {
    min-width: 220px;
  }
</style>
